import { can } from "@/app/auth/can";
import type { UserEntity } from "@/shared/types/user.entity";

import { type PayoutBatch, type PayoutBatchSummary, PayoutBatchStatus } from "../types/payoutBatches.types";

/**
 * Which permission each payout batch affordance needs, taken from the `@JwtAuth` decorators of
 * `payout-batches.internal.controller.ts` (coloca-nest). Default deny, like every helper built on `can`.
 *
 * - `payout_batches:read` — list, detail, rows, trail, original file, report, template.
 * - `payout_batches:write` — upload, selection, revalidate, submit for approval, withdraw, retry preview,
 *   cancel.
 * - `payout_batches:approve:write` — execute, reject, retry, resume.
 *
 * Dual control: whoever uploaded a batch or sent it for approval does not execute it nor retry its rows
 * (`self_approval_forbidden` in the backend), unless the viewer's `approval_policy` lets them — a member
 * whose organization allows it, or who is its only approver. That maker then approves straight from the
 * review. Sending a batch back is never the maker's: they withdraw it instead. Resume re-enqueues rows
 * someone already approved, so the maker may resume.
 */

type BatchMakers = Pick<PayoutBatch, "created_by_user_id" | "submitted_by_user_id">;

type BatchApprovalSubject = BatchMakers & {
  summary?: Pick<PayoutBatchSummary, "approval_policy"> | null;
};

export function canWriteBatches(user: UserEntity | null | undefined): boolean {
  return can(user, "payout_batches:write");
}

export function canApproveBatches(user: UserEntity | null | undefined): boolean {
  return can(user, "payout_batches:approve:write");
}

export function isBatchUploader(
  user: UserEntity | null | undefined,
  batch: Pick<PayoutBatch, "created_by_user_id">,
): boolean {
  return !!user && user.id === batch.created_by_user_id;
}

export function isBatchSubmitter(
  user: UserEntity | null | undefined,
  batch: Pick<PayoutBatch, "submitted_by_user_id">,
): boolean {
  return !!user && !!batch.submitted_by_user_id && user.id === batch.submitted_by_user_id;
}

/** Uploaded it or sent it for approval: the maker's side of dual control. */
export function isBatchMaker(user: UserEntity | null | undefined, batch: BatchMakers): boolean {
  return isBatchUploader(user, batch) || isBatchSubmitter(user, batch);
}

/** The backend's answer for this viewer; a missing policy (an older backend) is a no. */
export function selfApprovalAllowed(batch: BatchApprovalSubject): boolean {
  return batch.summary?.approval_policy?.self_approval_allowed === true;
}

/**
 * Execute and retry: the approval permission, on a batch someone else made or one the policy lets them
 * approve.
 */
export function canApproveThisBatch(
  user: UserEntity | null | undefined,
  batch: BatchApprovalSubject,
): boolean {
  return canApproveBatches(user) && (!isBatchMaker(user, batch) || selfApprovalAllowed(batch));
}

export function canRejectThisBatch(user: UserEntity | null | undefined, batch: BatchMakers): boolean {
  return canApproveBatches(user) && !isBatchMaker(user, batch);
}

/** Approve and pay from the review, in one step: only its maker, and only when the policy lets them. */
export function canApproveFromReview(
  user: UserEntity | null | undefined,
  batch: BatchApprovalSubject & Pick<PayoutBatch, "status">,
): boolean {
  return (
    batch.status === PayoutBatchStatus.READY &&
    canApproveBatches(user) &&
    isBatchMaker(user, batch) &&
    selfApprovalAllowed(batch)
  );
}
